import { Link, useParams } from 'react-router-dom';
import { ArticleNavigation } from '../components/article/ArticleNavigation';
import { mdxComponents } from '../components/article/mdxComponents';
import { RelatedArticles } from '../components/article/RelatedArticles';
import { TableOfContents } from '../components/article/TableOfContents';
import { HelpLayout } from '../components/layout/HelpLayout';
import { Breadcrumbs } from '../components/navigation/Breadcrumbs';
import { articles, getArticleByRoute } from '../lib/content';
import { profileFromSegment, profileHomePath } from '../lib/routes';
import { ARTICLE_TYPE_LABEL } from '../types/article';
import { PROFILE_LABEL } from '../types/profile';

const ARTICLE_CONTENT_ID = 'article-content';

export function ArticlePage() {
  const { profileSegment, category, articleSlug } = useParams();
  const profile = profileFromSegment(profileSegment);

  if (!profile || !category || !articleSlug) return <NotFound />;

  const article = getArticleByRoute(profile, category, articleSlug);
  if (!article) {
    const existsForAnotherProfile = articles.some(
      (item) => item.meta.status === 'published' && item.meta.category === category && item.meta.slug === articleSlug,
    );

    return existsForAnotherProfile ? (
      <HelpLayout profile={profile}>
        <Breadcrumbs profile={profile} />
        <section className="empty-state">
          <p className="eyebrow">Conteúdo de outra alçada</p>
          <h1>Este conteúdo não pertence a este perfil.</h1>
          <p>Você está visualizando a ajuda como {PROFILE_LABEL[profile]}.</p>
          <Link className="button-link" to={profileHomePath(profile)}>Voltar para minha ajuda</Link>
        </section>
      </HelpLayout>
    ) : <NotFound profile={profile} />;
  }

  const { Component, meta } = article;
  return (
    <HelpLayout profile={profile}>
      <Breadcrumbs profile={profile} category={meta.category} title={meta.title} />
      <div className="article-with-toc">
        <div className="article-column">
          <header className="article-header">
            <div className="article-meta">
              <span>{PROFILE_LABEL[meta.profile]}</span>
              <span>{ARTICLE_TYPE_LABEL[meta.type]}</span>
            </div>
            <p className="article-description">{meta.description}</p>
          </header>

          <article id={ARTICLE_CONTENT_ID} className="article-body">
            <Component components={mdxComponents} />
          </article>

          <footer className="article-footer">
            Atualizado em {new Date(`${meta.updatedAt}T12:00:00`).toLocaleDateString('pt-BR')}
          </footer>
          <RelatedArticles profile={profile} ids={meta.related} />
          <ArticleNavigation profile={profile} articleId={meta.id} />
        </div>

        <aside className="article-toc-column">
          <TableOfContents contentId={ARTICLE_CONTENT_ID} articleKey={meta.id} />
        </aside>
      </div>
    </HelpLayout>
  );
}

function NotFound({ profile }: { profile?: ReturnType<typeof profileFromSegment> }) {
  return (
    <main className="page-shell empty-state">
      <p className="eyebrow">404</p>
      <h1>Não encontramos esta página.</h1>
      <p>O conteúdo pode ter sido movido ou o endereço está incorreto.</p>
      <Link className="button-link" to={profile ? profileHomePath(profile) : '/'}>Ir para o Centro de Ajuda</Link>
    </main>
  );
}
